import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, DollarSign, Clock, CheckCircle, ChevronRight, PlusCircle } from 'lucide-react';
import { bookingService } from '../../services/bookingService';
import { commissionService } from '../../services/commissionService';
import { useAuth } from '../../context/AuthContext';
import { UI, buttonStyle, ui } from '../../utils/ui';

const statusPill = (status) => {
  const map = {
    confirmed: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
    pending: { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fde047' },
    completed: { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' },
    cancelled: { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' },
  };
  const c = map[status] || map.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const AgentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [commSummary, setCommSummary] = useState({ total_earned: 0, pending_amount: 0, paid_amount: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      bookingService.getAgentBookings(),
      commissionService.getMyCommissions(),
    ])
      .then(([bRes, cRes]) => {
        const bookings = bRes.data.bookings || [];
        setRecentBookings(bookings.slice(0, 5));
        setStats({
          total: bookings.length,
          pending: bookings.filter((b) => b.booking_status === 'pending').length,
          confirmed: bookings.filter((b) => b.booking_status === 'confirmed').length,
          completed: bookings.filter((b) => b.booking_status === 'completed').length,
        });
        setCommSummary(cRes.data.summary || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const statCards = useMemo(() => ([
    { label: 'Total Bookings', value: stats.total, icon: BookOpen, accent: UI.colors.info, bg: 'rgba(37,99,235,0.12)' },
    { label: 'Pending', value: stats.pending, icon: Clock, accent: UI.colors.warning, bg: 'rgba(245,158,11,0.14)' },
    { label: 'Confirmed', value: stats.confirmed, icon: CheckCircle, accent: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
    { label: 'Commission Earned', value: `₹${Number(commSummary.total_earned || 0).toLocaleString()}`, icon: DollarSign, accent: '#7c3aed', bg: 'rgba(124,58,237,0.12)' },
  ]), [commSummary.total_earned, stats.confirmed, stats.pending, stats.total]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
  const headerStyle = { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };
  const sectionTitleStyle = { fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text, margin: 0 };
  const textLinkStyle = { fontSize: 13, fontWeight: 900, color: UI.colors.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 };

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Agent Dashboard</h1>
          <p style={subtitleStyle}>
            Welcome back, <span style={{ color: UI.colors.primary, fontWeight: 900 }}>{user?.name}</span>!
          </p>
        </div>
        <Link to="/agent/create-booking" style={{ ...buttonStyle('primary'), padding: '10px 14px', borderRadius: 14 }}>
          <PlusCircle size={18} /> Create Booking
        </Link>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {statCards.map((s) => (
          <div key={s.label} style={{ ...ui.card, padding: 20 }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, backgroundColor: s.bg, color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <s.icon size={22} />
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 24, fontWeight: 900, color: UI.colors.text }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Commission Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        <div style={{ ...ui.card, padding: 22, backgroundColor: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.28)' }}>
          <p style={{ margin: 0, color: UI.colors.warning, fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Pending Commission</p>
          <p style={{ margin: '10px 0 0', fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: UI.colors.text }}>₹{Number(commSummary.pending_amount || 0).toLocaleString()}</p>
        </div>
        <div style={{ ...ui.card, padding: 22, backgroundColor: 'rgba(22,163,74,0.12)', borderColor: 'rgba(22,163,74,0.26)' }}>
          <p style={{ margin: 0, color: '#16a34a', fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Paid Commission</p>
          <p style={{ margin: '10px 0 0', fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: UI.colors.text }}>₹{Number(commSummary.paid_amount || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Recent Bookings */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <h2 style={sectionTitleStyle}>Recent Bookings</h2>
            <Link to="/agent/commissions" style={textLinkStyle}>
              Commissions <ChevronRight size={16} />
            </Link>
          </div>

          {recentBookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '26px 10px', color: UI.colors.muted }}>
              <BookOpen size={40} color="#9ca3af" style={{ opacity: 0.35, margin: '0 auto 10px' }} />
              <p style={{ margin: '0 0 12px', fontWeight: 900 }}>No bookings yet.</p>
              <Link to="/agent/create-booking" style={{ ...buttonStyle('primary'), padding: '10px 14px', borderRadius: 14 }}>
                Create First Booking
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 640 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 12, padding: '10px 12px', borderRadius: 14, backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`, fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>
                  <span>Reference</span><span>Customer</span><span>Room</span><span>Check-in</span><span>Amount</span><span>Status</span>
                </div>
                {recentBookings.map((b) => (
                  <div key={b.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 12, padding: '12px 12px', borderBottom: `1px solid ${UI.colors.border}`, alignItems: 'center', fontSize: 13 }}>
                    <span style={{ color: UI.colors.primary, fontWeight: 900, fontSize: 12 }}>{b.booking_reference}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.customer_name}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.room_name}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.check_in}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 900 }}>₹{Number(b.total_amount).toLocaleString()}</span>
                    <span style={statusPill(b.booking_status)}>{b.booking_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;

